import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { StoreDispatch } from '../../store/configure-store'
import { FromDropdown } from '../forms/input/form-dropdown'
import { RequiredAsterisk } from '../forms/required-asterisk'
import { Backlog } from '../util-cmps/backlog'
import { Spinner } from '../util-cmps/spinner'
import { setUserMsg } from '../../store/user'
import { newHttpService } from '../../services/new-http-service'
import { utilService } from '../../services/util-service'
import { updateAuditControlMutation } from '../util-cmps/graphql-mutations/update-audit-control'
import { useEscapeKey } from '../../hooks/use-escape-key'
import { useScrollLock } from '../../hooks/use-scroll-lock'
import { useFocusTrap } from '../../hooks/use-focus-trap'
import { useDraggable } from '../../hooks/use-draggable'
import { useCachedFetch } from '../../hooks/use-cached-fetch'
import { isRequiredSystem, SystemTypes } from '../forms/is-required-system'
import { ConsolidationConfigBuilder, validateConsolidationPairings } from './consolidation-config-builder'
import { AdditionalInfoEditor, serializeAdditionalInfo } from './additional-info-editor'
import { getFullAuditControlsByCompanyNameQuery } from '../util-cmps/graphql-queries/audit-controls-query'
import { AuditControlAuditHistoryModal } from './audit-control-audit-history-modal'

interface AuditControlEditModalProps {
    companyName: string
    auditControl: any
    onSuccess: () => void
    onClose: () => void
}

const toFormattedJson = (val: any): string => {
    if (val == null || val === '') return '[]'
    if (typeof val === 'string') {
        try { return JSON.stringify(JSON.parse(val), null, 4) } catch { return val }
    }
    return JSON.stringify(val, null, 4)
}

/** Returns system types for consolidation builder based on actually selected systems */
const getSelectedSystemTypes = (mainSystemId: string, hrSystemId: string, ticketSystemId: string): string[] => {
    const types: string[] = []
    if (mainSystemId) types.push(SystemTypes.IN_SCOPE)
    if (hrSystemId) types.push(SystemTypes.HR)
    if (ticketSystemId) types.push(SystemTypes.TICKET)
    return types.length > 0 ? types : [SystemTypes.IN_SCOPE]
}

export const AuditControlEditModal: React.FC<AuditControlEditModalProps> = ({
    companyName, auditControl, onSuccess, onClose
}) => {
    const dispatch = useDispatch<StoreDispatch>()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [lastModifiedSnippet, setLastModifiedSnippet] = useState<{ actor: string; time: string } | null>(null)
    const [isHistoryOpen, setIsHistoryOpen] = useState(false)

    const entityId = auditControl.audit?.entity?.id
    const entityName = auditControl.audit?.entity?.name || ''
    const objectiveName = auditControl.control?.objective?.name || ''
    const objectiveNumber = auditControl.control?.objective?.objectiveNumber ?? null

    // controlOwner/controlIt have DB integer IDs, but users list has Cognito UUID IDs.
    // We match by email to find the correct UUID for pre-selection.
    const controlOwnerEmail = auditControl.controlOwner?.email || ''
    const controlItEmail = auditControl.controlIt?.email || ''

    const [form, setForm] = useState(() => ({
        mainSystemId: String(auditControl.control?.mainSystem?.id || ''),
        hrSystemId: String(auditControl.control?.hrSystem?.id || ''),
        ticketSystemId: String(auditControl.control?.ticketSystem?.id || ''),
        consolidationJson: toFormattedJson(auditControl.control?.systemConsolidations),
        auditId: String(auditControl.audit?.id || ''),
        controlOwnerId: '', // resolved after users load
        controlItId: '', // resolved after users load
        isVisible: String(!(auditControl.isHidden ?? false)),
        isAutomated: String(auditControl.isAutomated ?? true),
        isCreationEqualsOnboardingApproval: String(auditControl.control?.isCreationEqualsOnboardingApproval ?? false),
        companyControlId: auditControl.control?.companyControlId || '',
        dashboardAdditionalInfo: auditControl.dashboardAdditionalInfo ?? auditControl.dashboard_additional_info ?? null,
        controlResultsAdditionalInfo: auditControl.controlResultsAdditionalInfo ?? auditControl.control_results_additional_info ?? null,
    }))

    const selectedSystemTypes = getSelectedSystemTypes(form.mainSystemId, form.hrSystemId, form.ticketSystemId)

    // Compute missing required systems for partial warning
    const missingSystems: string[] = []
    if (isRequiredSystem(objectiveNumber, SystemTypes.IN_SCOPE) && !form.mainSystemId) missingSystems.push('Main System')
    if (isRequiredSystem(objectiveNumber, SystemTypes.HR) && !form.hrSystemId) missingSystems.push('HR System')
    if (isRequiredSystem(objectiveNumber, SystemTypes.TICKET) && !form.ticketSystemId) missingSystems.push('Ticket System')

    // Load entity systems, audit years, and users for dropdowns
    const { data: modalData, isLoading, hasError } = useCachedFetch(
        `ac-edit-modal:${companyName}:${entityId}`,
        async () => {
            const query = `{
                companies(name: "${companyName}") {
                        entity(sorting: ASC) {
                            id
                            name
                            entitySystems(sorting: ASC) {
                                id
                                system { id name type }
                            }
                        audit(sorting: DESC) {
                            id
                            year
                        }
                    }
                    user {
                        id
                        email
                        firstName
                        lastName
                    }
                }
            }`
            const result = await newHttpService.graphql(companyName, query)
            const company = result.companies?.[0]
            return {
                entities: company?.entity || [],
                users: company?.user || [],
            }
        }
    )

    const { data: fullControls } = useCachedFetch(
        `ac-edit-full:${companyName}`,
        async () => {
            const result = await newHttpService.graphql(companyName, getFullAuditControlsByCompanyNameQuery(companyName))
            return result.auditControlsByCompanyName || []
        }
    )
    const [additionalInfoHydrated, setAdditionalInfoHydrated] = useState(false)

    // Resolve controlOwner/controlIt by email once users are loaded
    const usersLoaded = !!modalData?.users?.length
    const [userIdsResolved, setUserIdsResolved] = useState(false)
    // Capture the resolved UUIDs so `clearControlIt` can compare "resolved-at-open" vs
    // "resolved-at-save". Using auditControl.controlIt.id directly would be the Employee
    // integer FK (old column), which doesn't correspond to form.controlItId (Cognito UUID),
    // so if email → user resolution fails we'd falsely infer a user-driven clear.
    const initialResolvedRef = useRef({ controlItId: '' })
    if (usersLoaded && !userIdsResolved) {
        const allUsers = modalData.users
        const ownerMatch = allUsers.find((u: any) => u.email === controlOwnerEmail)
        const itMatch = allUsers.find((u: any) => u.email === controlItEmail)
        const resolvedItId = itMatch ? String(itMatch.id) : ''
        initialResolvedRef.current = { controlItId: resolvedItId }
        setForm(prev => ({
            ...prev,
            controlOwnerId: ownerMatch ? String(ownerMatch.id) : '',
            controlItId: resolvedItId,
        }))
        setUserIdsResolved(true)
    }

    useEscapeKey(onClose)
    useScrollLock()
    const trapRef = useFocusTrap<HTMLDivElement>()
    const { modalRef, onHeaderMouseDown } = useDraggable<HTMLElement>()

    const selectedEntity = modalData?.entities?.find((e: any) => e.id === entityId)
    const entitySystems = selectedEntity?.entitySystems || []
    const auditYears = selectedEntity?.audit || []
    const users = modalData?.users || []

    const entitySystemOptions = entitySystems.map((es: any) => ({ id: String(es.id), name: es.system.name, type: es.system.type }))
    const auditYearOptions = auditYears.map((a: any) => ({ id: String(a.id), name: String(a.year) }))
    const userOptions = users.map((u: any) => ({ id: String(u.id), name: `${u.firstName} ${u.lastName}` }))
    const boolOptions = [{ id: 'true', name: 'Yes' }, { id: 'false', name: 'No' }]
    const normalizeSystemType = (value: string | null | undefined) => (value || '').trim().toLowerCase()
    const getFilteredEntitySystemOptions = (systemType: SystemTypes) => {
        const target = systemType === SystemTypes.HR ? 'hr' : systemType === SystemTypes.TICKET ? 'itsm' : null
        if (!target) return entitySystemOptions
        return entitySystemOptions.filter((option: any) => normalizeSystemType(option.type) === target)
    }

    const actorLabel = useCallback((email: string | null) => {
        if (!email) return 'Current admin'
        const local = email.split('@')[0]
        return local
            .split(/[._-]/)
            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
    }, [])

    const fullAuditControl = fullControls?.find((ac: any) => Number(ac.id) === Number(auditControl.id))
    const auditControlId = String(auditControl.id)

    useEffect(() => {
        if (additionalInfoHydrated || !fullAuditControl) return
        setForm(prev => ({
            ...prev,
            dashboardAdditionalInfo: fullAuditControl.dashboardAdditionalInfo ?? fullAuditControl.control?.dashboardAdditionalInfo ?? prev.dashboardAdditionalInfo,
            controlResultsAdditionalInfo: fullAuditControl.controlResultsAdditionalInfo ?? fullAuditControl.control?.controlResultsAdditionalInfo ?? prev.controlResultsAdditionalInfo,
        }))
        setAdditionalInfoHydrated(true)
    }, [additionalInfoHydrated, fullAuditControl])

    useEffect(() => {
        let active = true
        if (!auditControl?.id) {
            setLastModifiedSnippet(null)
            return
        }

        newHttpService.get('/api/backoffice/audit-log/latest', {
            company_name: companyName,
            resource_type: 'audit_control',
            resource_id: String(auditControl.id),
        })
            .then((latest: any) => {
                if (!active || !latest?.created_at) return
                setLastModifiedSnippet({
                    actor: latest.actor_name || actorLabel(latest.actor_email),
                    time: utilService.formatDateTime(latest.created_at) || '—',
                })
            })
            .catch(() => {
                if (!active) return
                setLastModifiedSnippet(null)
            })

        return () => {
            active = false
        }
    }, [auditControl?.id, actorLabel, companyName])

    const onChangeDropdown = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
    }

    const onConsolidationChange = (val: string) => {
        setForm(prev => ({ ...prev, consolidationJson: val }))
    }

    const validate = (): string | null => {
        if (isRequiredSystem(objectiveNumber, SystemTypes.IN_SCOPE) && !form.mainSystemId) return 'Main System is required'
        if (isRequiredSystem(objectiveNumber, SystemTypes.HR) && !form.hrSystemId) return 'HR System is required'
        // Ticket system is never blocking — missing it just makes the AC "partial"

        const hasHrSelected = !!form.hrSystemId
        const hasTicketSelected = !!form.ticketSystemId
        const hasMultipleSystems = hasHrSelected || hasTicketSelected

        if (hasMultipleSystems) {
            if (!form.consolidationJson || form.consolidationJson === '[]') return 'Consolidation config is required'
            const pairingError = validateConsolidationPairings(form.consolidationJson, hasHrSelected, hasTicketSelected)
            if (pairingError) return pairingError
        }

        if (!form.auditId) return 'Audit Year is required'
        if (!form.controlOwnerId || !userOptions.some((u: { id: string; name: string }) => u.id === form.controlOwnerId)) return 'Control Owner is required'

        return null
    }

    const onSubmit = async () => {
        const error = validate()
        if (error) {
            dispatch(setUserMsg({ msg: { type: 'danger', txt: error } }))
            return
        }

        // Confirm visibility change (matching table cell-click behavior)
        const initialIsVisible = String(!(auditControl.isHidden ?? false))
        if (form.isVisible !== initialIsVisible) {
            const action = form.isVisible === 'false' ? 'hide' : 'unhide'
            if (!window.confirm(`Are you sure you want to ${action} audit control ${auditControl.id}?`)) return
        }

        // Confirm active/inactive change (matching table cell-click behavior)
        const initialIsAutomated = String(auditControl.isAutomated ?? true)
        if (form.isAutomated !== initialIsAutomated) {
            const action = form.isAutomated === 'true' ? 'activate' : 'deactivate'
            if (!window.confirm(`Are you sure you want to ${action} audit control ${auditControl.id}?`)) return
        }

        // Remind the user when removing HR/Ticket: backend will also drop the matching
        // consolidation config rules, since they reference a system that no longer exists.
        const initialHrId = String(auditControl.control?.hrSystem?.id || '')
        const initialTicketId = String(auditControl.control?.ticketSystem?.id || '')
        const removed: string[] = []
        if (initialHrId && !form.hrSystemId) removed.push('HR')
        if (initialTicketId && !form.ticketSystemId) removed.push('Ticket')
        if (removed.length > 0) {
            const list = removed.join(' and ')
            const ruleWord = removed.length > 1 ? 'rules' : 'rule'
            if (!window.confirm(
                `Removing the ${list} system will also remove the corresponding ` +
                `consolidation config ${ruleWord}. Continue?`
            )) return
        }

        // Compute explicit clear flags for nullable FKs. The backend can't distinguish
        // "variable omitted" from "variable explicitly null" via Graphene kwargs alone, so
        // these booleans are the authoritative "clear this column" signal. We send either
        // an ID OR a clear flag per field — never both — so the payload's intent is
        // unambiguous when read in isolation.
        // For controlIt we compare against the resolved UUID captured at open time (ref),
        // not the raw Employee FK on `auditControl.controlIt` — otherwise an email/user
        // mismatch would surface as a fabricated "user cleared" intent on save.
        const initialControlItId = initialResolvedRef.current.controlItId

        setIsSubmitting(true)
        try {
            const query = updateAuditControlMutation()
            const variables: any = {
                auditControlId: Number(auditControl.id),
                mainSystemId: form.mainSystemId ? Number(form.mainSystemId) : null,
                systemConsolidations: form.consolidationJson,
                auditId: form.auditId ? Number(form.auditId) : null,
                controlOwnerId: form.controlOwnerId || null,
                isHidden: form.isVisible === 'false',
                isAutomated: form.isAutomated === 'true',
                isCreationEqualsOnboardingApproval: form.isCreationEqualsOnboardingApproval === 'true',
                companyControlId: form.companyControlId.trim() || null,
                dashboardAdditionalInfo: serializeAdditionalInfo(form.dashboardAdditionalInfo),
                controlResultsAdditionalInfo: serializeAdditionalInfo(form.controlResultsAdditionalInfo),
            }

            // HR system: either set or clear, exclusively. Omitted = unchanged.
            if (form.hrSystemId) variables.hrSystemId = Number(form.hrSystemId)
            else if (initialHrId) variables.clearHrSystem = true

            // Ticket system: same pattern.
            if (form.ticketSystemId) variables.ticketSystemId = Number(form.ticketSystemId)
            else if (initialTicketId) variables.clearTicketSystem = true

            // Control IT user: same pattern.
            if (form.controlItId) variables.controlItId = form.controlItId
            else if (initialControlItId) variables.clearControlIt = true

            await newHttpService.graphql(companyName, { query, variables })
            dispatch(setUserMsg({ msg: { type: 'success', txt: `Audit control ${auditControl.id} updated successfully` } }))
            onSuccess()
        } catch (err: any) {
            const serverMsg = err?.response?.data?.errors?.[0]?.message
            dispatch(setUserMsg({ msg: { type: 'danger', txt: serverMsg || 'Failed updating audit control' } }))
        } finally {
            setIsSubmitting(false)
        }
    }

    const modalTitle = `Edit Audit Control #${auditControl.id}`

    if (isLoading || hasError) {
        return <div ref={trapRef}>
            <section ref={modalRef} className="audit-control-form-modal edit-mode" role="dialog" aria-modal="true" aria-label={modalTitle}>
                <header onMouseDown={onHeaderMouseDown}>
                    <h2>{modalTitle}</h2>
                    <button className="close-btn" onClick={onClose} aria-label="Close">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </header>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    {isLoading && <Spinner />}
                    {hasError && <p>Failed to load form data</p>}
                </div>
            </section>
            <Backlog zIndex={199} onCloseBacklog={onClose} />
        </div>
    }

    return <div ref={trapRef}>

        <section ref={modalRef} className="audit-control-form-modal edit-mode" role="dialog" aria-modal="true" aria-label={modalTitle}>
            <header onMouseDown={onHeaderMouseDown}>
                <h2>{modalTitle}</h2>
                <button className="close-btn" onClick={onClose} aria-label="Close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>

            <section className="modal-form-body">
                <div className="form-section-card">
                    <div className="form-section-label">Entity & Objective</div>
                    <div className="form-row">
                        <div className="field">
                            <label>Entity</label>
                            <input type="text" value={entityName} disabled />
                        </div>
                        <div className="field">
                            <label>Objective</label>
                            <input type="text" value={objectiveName} disabled />
                        </div>
                        <div className="field">
                            <label>
                                Company Control ID
                                <span className="field-info-icon" data-tip="Optional. For companies that require an external reference ID for audit controls (e.g., ZIM uses this as a ref ID).">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                                    </svg>
                                </span>
                            </label>
                            <input type="text" value={form.companyControlId} onChange={(e) => setForm(prev => ({ ...prev, companyControlId: e.target.value }))} />
                        </div>
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Systems</div>
                    <div className="form-row">
                        <div className="field">
                            {/*
                              Main system cannot be changed after the audit control is created — it's
                              the in-scope system every consolidation/raw record is built around.
                              Render the dropdown disabled so the current value stays visible but grayed out.
                            */}
                            <div>
                                <label htmlFor="mainSystemId">Main System</label>
                                {isRequiredSystem(objectiveNumber, SystemTypes.IN_SCOPE) && <RequiredAsterisk />}
                            </div>
                            <select
                                name="mainSystemId"
                                id="mainSystemId"
                                value={form.mainSystemId}
                                disabled
                                title="Main system cannot be changed after creation"
                                onChange={() => {}}
                            >
                                <option value=""></option>
                                {entitySystemOptions.map((option: { id: string; name: string }) => (
                                    <option key={option.id} value={option.id}>{option.name}</option>
                                ))}
                            </select>
                            <span></span>
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="hrSystemId"
                                label="HR System"
                                isRequired={isRequiredSystem(objectiveNumber, SystemTypes.HR)}
                                options={getFilteredEntitySystemOptions(SystemTypes.HR)}
                                value={form.hrSystemId}
                                onSelectValue={onChangeDropdown}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="ticketSystemId"
                                label="Ticket System"
                                isRequired={isRequiredSystem(objectiveNumber, SystemTypes.TICKET)}
                                options={getFilteredEntitySystemOptions(SystemTypes.TICKET)}
                                value={form.ticketSystemId}
                                onSelectValue={onChangeDropdown}
                            />
                        </div>
                    </div>
                    {missingSystems.length > 0 && (
                        <div className="partial-warning">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e65100" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span>Partial setup — missing required system{missingSystems.length > 1 ? 's' : ''}: <strong>{missingSystems.join(', ')}</strong></span>
                        </div>
                    )}
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Consolidation Config</div>
                    <ConsolidationConfigBuilder
                        value={form.consolidationJson}
                        onChange={onConsolidationChange}
                        availableSystemTypes={selectedSystemTypes}
                    />
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Audit</div>
                    <div className="form-row">
                        <div className="field">
                            <FromDropdown
                                keyName="auditId"
                                label="Audit Year"
                                options={auditYearOptions}
                                value={form.auditId}
                                onSelectValue={onChangeDropdown}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="controlOwnerId"
                                label="Control Owner"
                                options={userOptions}
                                value={form.controlOwnerId}
                                onSelectValue={onChangeDropdown}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="controlItId"
                                label="Control IT"
                                isRequired={false}
                                options={userOptions}
                                value={form.controlItId}
                                onSelectValue={onChangeDropdown}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section-card additional-info-card">
                    <div className="form-section-label">Additional Info</div>
                    <AdditionalInfoEditor
                        dashboardValue={form.dashboardAdditionalInfo}
                        controlResultsValue={form.controlResultsAdditionalInfo}
                        onDashboardChange={(value) => setForm(prev => ({ ...prev, dashboardAdditionalInfo: value }))}
                        onControlResultsChange={(value) => setForm(prev => ({ ...prev, controlResultsAdditionalInfo: value }))}
                    />
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Settings</div>
                    <div className="form-row">
                        <div className="field">
                            <FromDropdown
                                keyName="isAutomated"
                                label="Active"
                                options={boolOptions}
                                value={form.isAutomated}
                                onSelectValue={onChangeDropdown}
                                hideBlankOption={true}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="isVisible"
                                label="Visible"
                                options={boolOptions}
                                value={form.isVisible}
                                onSelectValue={onChangeDropdown}
                                hideBlankOption={true}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="isCreationEqualsOnboardingApproval"
                                label="Creation Equals Onboarding Approval"
                                options={boolOptions}
                                value={form.isCreationEqualsOnboardingApproval}
                                onSelectValue={onChangeDropdown}
                                hideBlankOption={true}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    {!isSubmitting && <button className="main" onClick={onSubmit} disabled={isSubmitting}>Save</button>}
                    <div className="audit-control-audit-footer">
                        <span className="audit-control-audit-footer__text">
                            {lastModifiedSnippet
                                ? `Last modified by ${lastModifiedSnippet.actor} at ${lastModifiedSnippet.time}`
                                : 'No audit history yet.'}
                        </span>
                        <button
                            type="button"
                            className="audit-control-audit-footer__link"
                            onClick={() => setIsHistoryOpen(true)}
                        >
                            Open history
                        </button>
                    </div>
                    {isSubmitting && <span className="spinner"><Spinner isSmall={true} /></span>}
                </div>
            </section>
        </section>
        <Backlog zIndex={199} onCloseBacklog={onClose} />
        {isHistoryOpen && (
            <AuditControlAuditHistoryModal
                isOpen={isHistoryOpen}
                companyName={companyName}
                auditControlId={auditControlId}
                onClose={() => setIsHistoryOpen(false)}
            />
        )}
    </div>
}
